/**
 * What a reply did besides writing: a tool it ran (and how it went), the
 * work it started (queued, as the analyst who asked), an approval it asked
 * for. Each sits in the reply where it happened.
 */

import { Anchor, Badge, Group, Loader, Text } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconHandStop, IconPlayerPlay } from '@tabler/icons-react';

import type { BlockOf } from '@/types/copilot';
import type { CaseLink } from './markdown';

export function ToolChip({ block }: Readonly<{ block: BlockOf<'tool'> }>) {
  const icon =
    block.state === 'running' ? (
      <Loader size={10} />
    ) : block.state === 'ok' ? (
      <IconCheck size={12} color="var(--mantine-color-teal-6)" />
    ) : (
      <IconAlertTriangle
        size={12}
        color={`var(--mantine-color-${block.state === 'refused' ? 'orange' : 'red'}-6)`}
      />
    );
  return (
    <Group gap={6} wrap="nowrap" data-testid="copilot-tool">
      {icon}
      <Text size="xs" c="dimmed" lineClamp={1}>
        {block.label}
        {block.summary ? ` — ${block.summary}` : ''}
        {block.state === 'refused' ? ' (refused)' : block.state === 'error' ? ' (failed)' : ''}
      </Text>
    </Group>
  );
}

const TASK_COLOR: Record<string, string> = {
  queued: 'blue',
  running: 'blue',
  waiting_on_human: 'orange',
  done: 'teal',
  failed: 'red',
  cancelled: 'gray',
};

export function TaskChip({
  block,
  onOpenLink,
}: Readonly<{ block: BlockOf<'task'>; onOpenLink: (link: CaseLink) => void }>) {
  return (
    <Group gap={6} wrap="nowrap" data-testid="copilot-task">
      <IconPlayerPlay size={12} color="var(--mantine-color-blue-6)" />
      <Anchor
        component="button"
        type="button"
        size="xs"
        onClick={() => onOpenLink({ scheme: 'task', id: block.taskId })}
        style={{ textAlign: 'left' }}
      >
        Started: {block.label}
      </Anchor>
      <Badge size="xs" variant="light" color={TASK_COLOR[block.status] ?? 'gray'}>
        {block.status.replace(/_/g, ' ')}
      </Badge>
    </Group>
  );
}

export function AskChip({
  block,
  onOpenLink,
}: Readonly<{ block: BlockOf<'ask'>; onOpenLink: (link: CaseLink) => void }>) {
  return (
    <Group gap={6} wrap="nowrap" align="flex-start" data-testid="copilot-ask">
      <IconHandStop size={12} color="var(--mantine-color-orange-6)" style={{ marginTop: 3 }} />
      <Text size="xs">
        Waiting for a person&apos;s approval:{' '}
        <Anchor
          component="button"
          type="button"
          size="xs"
          onClick={() => onOpenLink({ scheme: 'ask', id: block.interventionId })}
          style={{ textAlign: 'left' }}
        >
          {block.question || block.label}
        </Anchor>
      </Text>
    </Group>
  );
}
